import { useEffect, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { CREATE_PLAYLIST_ADD_LESSON_PARAM, CREATE_PLAYLIST_ADD_SHORT_PARAM, CREATE_PLAYLIST_ADD_TRACK_PARAM, CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, MY_SAVED_ITEMS_PAGE_SIZE, useLatestSavedItemsInteractions, usePlaylist } from '.';
import { CREATE_PLAYLIST_URL_FRAGMENT, EDIT_PLAYLIST_URL_FRAGMENT, PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT, DUPLICATE_PLAYLIST_URL_FRAGMENT } from '../../constants/Navigation';
import { FILTER_CONSTS } from '../../constants/TopFilterConstants';
import { removeTrailingSlash } from '../../utils/RoutingUtils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useLesson } from '../lessons';
import { useTrack } from '../tracks';
import { usePlaylistProgress } from '../userProgress';
import { useMyPlaylists } from '../myPlaylists';
import { useShort } from '../shortContent';
export const useCloseCreatePlaylistModalPath = () => {
  const location = useLocation();
  return useMemo(() => {
    const {
      pathname: currentPathname
    } = location;
    const createPlaylistUrlFragmentIndex = currentPathname.indexOf(CREATE_PLAYLIST_URL_FRAGMENT);
    const editPlaylistUrlFragmentIndex = currentPathname.indexOf(EDIT_PLAYLIST_URL_FRAGMENT);
    const duplicatePlaylistUrlFragmentIndex = currentPathname.indexOf(DUPLICATE_PLAYLIST_URL_FRAGMENT);
    let originalUrl = currentPathname;
    if (createPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, createPlaylistUrlFragmentIndex);
    } else if (editPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, editPlaylistUrlFragmentIndex);
    } else if (duplicatePlaylistUrlFragmentIndex !== -1) {
      if (location.state && location.state.from) {
        originalUrl = location.state.from.pathname;
      } else if (originalUrl === null) {
        originalUrl = PLAYLIST_URL_FRAGMENT;
      }
    }
    const originalParams = new URLSearchParams(location.search);
    originalParams.delete(CREATE_PLAYLIST_ADD_TRACK_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ADD_LESSON_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ADD_SHORT_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM);
    return `${removeTrailingSlash(originalUrl) || '/'}${originalParams.size ? `?${originalParams}` : ''}`;
  }, [location]);
};
export const useCreatePlaylistModalPath = ({
  trackId,
  lessonId,
  shortId,
  language
} = {}) => {
  const location = useLocation();
  return useMemo(() => {
    let sourceUrl = removeTrailingSlash(location.pathname);
    if (sourceUrl === '/') sourceUrl = '';
    const params = new URLSearchParams(location.search);
    if (trackId) params.append(CREATE_PLAYLIST_ADD_TRACK_PARAM, trackId.toString());
    if (lessonId) params.append(CREATE_PLAYLIST_ADD_LESSON_PARAM, lessonId.toString());
    if (shortId) params.append(CREATE_PLAYLIST_ADD_SHORT_PARAM, shortId.toString());
    if (language) params.append(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, language);
    return `${sourceUrl}${CREATE_PLAYLIST_URL_FRAGMENT}${params.size ? `?${params}` : ''}`;
  }, [language, lessonId, location.pathname, location.search, shortId, trackId]);
};
export const useSavedItemsTooltip = () => {
  const {
    latestInteraction,
    removeLatestInteraction
  } = useLatestSavedItemsInteractions();
  const {
    itemId,
    playlistId,
    type,
    action,
    language,
    portalId,
    assignmentIds
  } = latestInteraction || {};
  const {
    playlist,
    playlistLoading
  } = usePlaylist(playlistId, !latestInteraction || !playlistId);
  const {
    track,
    trackLoading
  } = useTrack(itemId, language, type !== 'TRACK');
  const {
    lesson,
    lessonLoading
  } = useLesson(itemId, language, type !== 'LESSON');
  const {
    short,
    shortLoading
  } = useShort(itemId, language, type !== 'SHORT_FORM_CONTENT' || !language);
  const isLoading = playlistLoading || trackLoading || lessonLoading || shortLoading;
  const show = !!latestInteraction && !isLoading;
  const itemName = track ? track.name : lesson ? lesson.name : short ? short.name : '';
  const playlistName = playlist && playlist.name;
  return {
    show,
    removeLatestInteraction,
    action,
    itemName,
    itemId,
    itemType: type,
    playlistName,
    playlistId,
    portalId,
    assignmentIds
  };
};
export const useShowNavbarWatchLaterTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['ADD_TO_WATCH_LATER', 'REMOVE_FROM_WATCH_LATER'].includes(action);
};
export const useShowNavbarPlaylistsTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['SAVE_PLAYLIST', 'UNSAVE_PLAYLIST', 'ARCHIVE_PLAYLIST', 'UNARCHIVE_PLAYLIST', 'DUPLICATE_PLAYLIST', 'ADD_TO_PLAYLIST', 'REMOVE_FROM_PLAYLIST', 'CREATE_PLAYLIST_ASSIGNMENT'].includes(action);
};
export const useTimedLatestSavedItemsInteraction = bypass => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    latestInteraction,
    removeLatestInteraction
  } = useLatestSavedItemsInteractions();
  useEffect(() => {
    if (isMobile || latestInteraction && !bypass) {
      const timeout = setTimeout(() => {
        removeLatestInteraction();
      }, 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [latestInteraction, removeLatestInteraction, isMobile, bypass]);
  return latestInteraction ? latestInteraction.action : null;
};
export const useAddToPlaylistOptionList = ({
  page = 1,
  skip = false
}) => useMyPlaylists({
  tab: 'created-by-me',
  limit: MY_SAVED_ITEMS_PAGE_SIZE,
  page,
  skip
});
export const usePlaylistRemainingItemsAndProgress = playlistId => {
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const status = playlistProgress ? playlistProgress.status : null;
  const remainingProgressItems = playlistProgress ? playlistProgress.itemsProgress.filter(item => item.status !== 'COMPLETED') : [];
  const remainingProgressItemCount = remainingProgressItems.length;
  const totalProgressItemCount = playlistProgress ? playlistProgress.itemsProgress.length : 0;
  const progressAmount = status === FILTER_CONSTS.STATUSES.COMPLETED ? 1 : status === FILTER_CONSTS.STATUSES.IN_PROGRESS ? (totalProgressItemCount - remainingProgressItemCount) / totalProgressItemCount : 0;
  return {
    remainingProgressItems,
    remainingProgressItemCount,
    totalProgressItemCount,
    progressAmount,
    status,
    playlistProgressLoading
  };
};
export const useIsPlaylistRoute = () => {
  const match = useRouteMatch([PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT]);
  return match ? match.isExact : false;
};